import { useMutation, useQuery } from '@apollo/client/react'
import { Loader, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import SettingToggleCard from '~/components/SettingToggleCard'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
  GQL_REMINDER_SETTINGS,
  GQL_SEND_REMINDER_TEST,
  GQL_UPDATE_REMINDER_SETTINGS,
} from '~/gql/reminders/reminders'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useSyncOnChange } from '~/hooks/use-sync-on-change'

/** Placeholders injectables dans l'objet et le corps, affichés en aide. */
const PLACEHOLDERS = [
  '{{firstName}}',
  '{{lastName}}',
  '{{moduleTitle}}',
  '{{sessionName}}',
  '{{endDate}}',
  '{{appName}}',
  '{{link}}',
]

type FormState = {
  enabled: boolean
  firstDelayDays: number
  frequencyDays: number
  maxReminders: number
  sendHour: number
  subject: string
  body: string
  completionDigestEnabled: boolean
  convocationEnabled: boolean
}

const EMPTY_FORM: FormState = {
  enabled: false,
  firstDelayDays: 3,
  frequencyDays: 7,
  maxReminders: 3,
  sendHour: 9,
  subject: '',
  body: '',
  completionDigestEnabled: false,
  convocationEnabled: true,
}

/** Borne une saisie numérique, en retombant sur `fallback` si le champ est vidé. */
function toNumber(raw: string, fallback: number, min: number, max: number): number {
  const parsed = Number.parseInt(raw, 10)
  if (Number.isNaN(parsed)) return fallback
  return Math.min(max, Math.max(min, parsed))
}

function ReminderSettings() {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_REMINDER_SETTINGS)
  const [updateSettings, { loading: saving }] = useMutation(GQL_UPDATE_REMINDER_SETTINGS)
  const [sendTest, { loading: testing }] = useMutation(GQL_SEND_REMINDER_TEST)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  useSyncOnChange(data?.reminderSettings, settings => setForm({
    enabled: settings.enabled ?? false,
    firstDelayDays: settings.firstDelayDays ?? 3,
    frequencyDays: settings.frequencyDays ?? 7,
    maxReminders: settings.maxReminders ?? 3,
    sendHour: settings.sendHour ?? 9,
    subject: settings.subject ?? '',
    body: settings.body ?? '',
    completionDigestEnabled: settings.completionDigestEnabled ?? false,
    convocationEnabled: settings.convocationEnabled ?? true,
  }))

  const set = <K extends keyof FormState>(key: K) => (value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  const toInput = () => ({
    enabled: form.enabled,
    firstDelayDays: form.firstDelayDays,
    frequencyDays: form.frequencyDays,
    maxReminders: form.maxReminders,
    sendHour: form.sendHour,
    subject: form.subject || undefined,
    body: form.body || undefined,
    completionDigestEnabled: form.completionDigestEnabled,
    convocationEnabled: form.convocationEnabled,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateSettings({ variables: { input: toInput() } })
      await refetch()
      toast.success(t('reminders.saved'))
    } catch {
      toast.error(t('reminders.saveError'))
    }
  }

  /** Envoie le message courant (non sauvegardé) à l'admin connecté. */
  const handleTest = async () => {
    try {
      const res = await sendTest({ variables: { input: toInput() } })
      const result = res.data?.sendReminderTest
      if (result?.sent) {
        toast.success(t('reminders.testSent', { email: result.to }))
      } else {
        toast.error(t('reminders.testError'))
      }
    } catch {
      toast.error(t('reminders.testError'))
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
      <Card {...tourAnchor('reminders.cadence')}>
        <CardHeader>
          <CardTitle>{t('reminders.cadence.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={e => set('enabled')(e.target.checked)}
            />
            {t('reminders.field.enabled')}
          </label>
          <p className="text-xs text-muted-foreground">{t('reminders.enabled.help')}</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="firstDelayDays">{t('reminders.field.firstDelayDays')}</Label>
              <Input
                id="firstDelayDays"
                type="number"
                min={0}
                max={365}
                value={form.firstDelayDays}
                onChange={e => set('firstDelayDays')(toNumber(e.target.value, 0, 0, 365))}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="frequencyDays">{t('reminders.field.frequencyDays')}</Label>
              <Input
                id="frequencyDays"
                type="number"
                min={3}
                max={365}
                value={form.frequencyDays}
                onChange={e => set('frequencyDays')(toNumber(e.target.value, 7, 3, 365))}
              />
              <p className="text-xs text-muted-foreground">{t('reminders.frequencyDays.help')}</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="maxReminders">{t('reminders.field.maxReminders')}</Label>
              <Input
                id="maxReminders"
                type="number"
                min={1}
                max={10}
                value={form.maxReminders}
                onChange={e => set('maxReminders')(toNumber(e.target.value, 3, 1, 10))}
              />
              <p className="text-xs text-muted-foreground">{t('reminders.maxReminders.help')}</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sendHour">{t('reminders.field.sendHour')}</Label>
              <Input
                id="sendHour"
                type="number"
                min={0}
                max={23}
                value={form.sendHour}
                onChange={e => set('sendHour')(toNumber(e.target.value, 0, 0, 23))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card {...tourAnchor('reminders.message')}>
        <CardHeader>
          <CardTitle>{t('reminders.message.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reminderSubject">{t('reminders.field.subject')}</Label>
            <Input
              id="reminderSubject"
              value={form.subject}
              onChange={e => set('subject')(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reminderBody">{t('reminders.field.body')}</Label>
            <textarea
              id="reminderBody"
              value={form.body}
              onChange={e => set('body')(e.target.value)}
              rows={10}
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t('reminders.placeholders.help')}
              {' '}
              {PLACEHOLDERS.join(' ')}
            </p>
          </div>
        </CardContent>
      </Card>

      <SettingToggleCard
        title={t('reminders.digest.title')}
        label={t('reminders.field.completionDigestEnabled')}
        help={t('reminders.digest.help')}
        checked={form.completionDigestEnabled}
        onCheckedChange={set('completionDigestEnabled')}
        {...tourAnchor('reminders.digest')}
      />

      <SettingToggleCard
        title={t('reminders.convocation.title')}
        label={t('reminders.field.convocationEnabled')}
        help={t('reminders.convocation.help')}
        checked={form.convocationEnabled}
        onCheckedChange={set('convocationEnabled')}
        {...tourAnchor('reminders.convocation')}
      />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={handleTest} disabled={testing} {...tourAnchor('reminders.test')}>
          {testing
            ? <Loader className="mr-2 size-4 animate-spin" />
            : <Send className="mr-2 size-4" />}
          {t('reminders.sendTest')}
        </Button>
        <Button type="submit" disabled={saving} {...tourAnchor('reminders.save')}>
          {saving && <Loader className="mr-2 size-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </form>
  )
}

export default ReminderSettings
